import { RestQueryContext } from '@/providers/restQuery.provider'
import { REST_QUERY_CONFIG, useQueryKey, useUrlWithParams } from '@/shared/helpers/restQuery'

import { useContext } from 'react'

import { type UseQueryOptions, useQuery } from '@tanstack/react-query'

/**
 * Hook for deferred (lazy) REST requests, triggered manually.
 *
 * This hook creates a TanStack Query request disabled by default, which only
 * fires on an explicit call to the fetch function. Useful for requests
 * triggered by user actions (clicks, submissions).
 * It uses the central REST client of the RestQueryProvider.
 *
 * Features:
 * - Request disabled by default (enabled: false)
 * - Manual trigger through the fetch function
 * - Central Axios client through RestQueryProvider
 * - 60-second stale time by default
 * - Full support of TanStack Query options
 * - Automatic caching and state handling
 * - Generic types for the response
 *
 * @param queryKey - Unique key of the request (TanStack Query cache)
 * @param url - URL of the endpoint to call
 * @param params
 * @param options - TanStack Query options, with a lazy prop for documentation
 * @returns The query result, with a fetch function to trigger it
 *
 * @example
 * ```tsx
 * const {
 *   data,
 *   error,
 *   isLoading,
 *   fetch
 * } = useLazyRestQuery<User>(
 *   ['user', userId],
 *   `/api/users/${userId}`,
 *   { retry: 3 }
 * )
 *
 * const handleLoadUser = async () => {
 *   const result = await fetch()
 *   if (result.data) {
 *     console.log('User loaded:', result.data)
 *   }
 * }
 *
 * return (
 *   <div>
 *     <button onClick={handleLoadUser} disabled={isLoading}>
 *       {isLoading ? 'Loading...' : 'Load user'}
 *     </button>
 *     {data && <UserProfile user={data} />}
 *     {error && <Error message={error.message} />}
 *   </div>
 * )
 * ```
 */
export function useLazyRestQuery<T>(
  queryKey: unknown[],
  url: string,
  params?: Record<string, string | number | boolean>,
  options?: UseQueryOptions<T> & { lazy?: true },
) {
  const api = useContext(RestQueryContext)

  const finalUrl = useUrlWithParams(url, params)
  const finalQueryKey = useQueryKey(queryKey, params)

  const query = useQuery<T>({
    queryKey: finalQueryKey,
    queryFn: async () => {
      const res = await api!.axiosInstance.get<T>(finalUrl)
      return res.data
    },
    enabled: false,
    staleTime: REST_QUERY_CONFIG.staleTime,
    ...options,
  })

  return {
    ...query,
    fetch: query.refetch,
  }
}
